/**
 * An ask whose computer job outgrows one reply continues out of sight (EFax 9, B5, 2026-10-08).
 *
 * A turn whose computer job cannot finish in its own time hands the job on to hops on the task lane instead of
 * answering part of it (functions/src/prest0nSession/computerChainHop.ts). The ask's own task stays `running` and names
 * the working hop in `dispatchMetadata.computerContinuedBy`; the chain's last hop answers on the ask's own task.
 *
 * On the streaming lane the server does that only for a browser that says it can follow the ask on
 * (`canFollowContinuation` on the turn): told `turn_continuing` with the ask's own task id, this browser polls that
 * task exactly as it polls a queued one, until the answer lands there. A browser that does not say so (an older
 * bundle, or this lever off) gets its reply inline, as before.
 *
 * Build-time lever `REACT_APP_ASK_CONTINUE` (deploy.yml, repo variable `ASK_CONTINUE`): absent or empty = on; `off`
 * stops this browser from saying it, so every streaming turn answers inline again. The task lane's chain is not
 * touched by it, and no chain starts anywhere the server's lever `ops/computerChain` leaves out.
 */
import { buildEnv } from '../../config/buildEnv';

export const ASK_CONTINUE_ENV = 'REACT_APP_ASK_CONTINUE';

/** May this browser follow an ask that continues out of sight? */
export function askContinueEnabled(env: Record<string, string | undefined> = buildEnv()): boolean {
  return String(env[ASK_CONTINUE_ENV] ?? '').trim().toLowerCase() !== 'off';
}

/**
 * How long a poll follows an ask that continues out of sight. A computer job's chain is limited to at most 45 minutes
 * from the ask (computerChainLever.ts); since 2026-10-10 an ask whose own work keeps moving (a filing job of fifty
 * faxes) may run to its working ceiling, 120 minutes (functions computerChain.ts CHAIN_WORKING_MINUTES_CEILING). Its
 * last hop starts up to five minutes before that and runs at most one task envelope (nine minutes); 140 minutes leaves
 * room over all of it. An ordinary task keeps its own limit (about 35 minutes).
 */
export const CHAIN_FOLLOW_MAX_POLL_MS = 140 * 60_000;

/** PURE. Is this task the original of an ask that continues on a hop right now? */
export function taskContinuesOutOfSight(task: unknown): boolean {
  if (!task || typeof task !== 'object') return false;
  const record = task as { status?: unknown; dispatchMetadata?: unknown };
  if (record.status !== 'running' && record.status !== 'pending') return false;
  const meta = record.dispatchMetadata;
  if (!meta || typeof meta !== 'object') return false;
  const hop = (meta as { computerContinuedBy?: unknown }).computerContinuedBy;
  return typeof hop === 'string' && hop.trim().length > 0;
}
